import React, { useEffect, useMemo, useState } from 'react';
import { Space, List, Row, Button, Spin, notification, Badge, Typography } from 'antd';
import { ScheduleItemView } from './ScheduleItemView';
import { _T } from "../../../../../base-react/src/Localization";
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { apis, models } from "@vote4dance/judging-sdk";
import { DownOutlined, EditOutlined, MenuOutlined, RightOutlined } from '@ant-design/icons';
import { ScheduleItemEdit } from './ScheduleItemEdit';
import { ScheduleItem } from '../../../../../base-react/models/schedule';
var Api = apis.Api;
var ScheduleSource = models.schedules.ScheduleSource;
import { getFormattedTime } from '../../../../../base-react/hooks/Schedule';
const { Text } = Typography;
export const ScheduleList = (props) => {
    const { event_id, currentPageData, schedule, isEditing, disabled } = props;
    const [defaultActiveKeys, setDefaultActiveKeys] = useState(null);
    const [sortedList, setSortedList] = useState([]);
    const mouseSensor = useSensor(MouseSensor);
    const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor);
    const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);
    useEffect(() => {
        setSortedList(currentPageData);
    }, [currentPageData]);
    useEffect(() => {
        if (currentPageData && currentPageData.length > 0) {
            const importantItems = currentPageData.filter(scheduleItem => scheduleItem.schedule_id === 'null' ||
                scheduleItem.schedule_rounds_ready > 0 ||
                (scheduleItem.schedule_rounds_confirmed > 0 &&
                    scheduleItem.schedule_rounds_confirmed < scheduleItem.schedule_rounds_total));
            setDefaultActiveKeys(importantItems.map(scheduleItem => scheduleItem.schedule_id));
        }
    }, [currentPageData]);
    const setItemLoading = (activeId, loading) => {
        if (sortedList && activeId) {
            setSortedList(list => list.map((item) => {
                return {
                    ...item,
                    dragLoading: loading && item.schedule_id == activeId
                };
            }));
        }
    };
    function handleDragEnd({ active, over }) {
        var _a, _b;
        if (((_b = (_a = active === null || active === void 0 ? void 0 : active.data) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.type) === 'round') {
            return;
        }
        if (!over) {
            return;
        }
        const scheduleIds = currentPageData.map((e) => e.schedule_id);
        const newList = arrayMove(currentPageData, scheduleIds.indexOf(active.id), scheduleIds.indexOf(over.id))
            .map((item) => {
            return {
                ...item,
                dragLoading: item.schedule_id == active.id
            };
        });
        setSortedList(newList);
        const allScheduleIds = schedule ? schedule.map((e) => e.schedule_id) : [];
        const newAllList = arrayMove(schedule, allScheduleIds.indexOf(active.id), allScheduleIds.indexOf(over.id));
        const scheduleSource = ScheduleSource(event_id);
        Api.call(scheduleSource.url, "set-order", newAllList.map(e => e.schedule_id))
            .catch(err => {
            setItemLoading(active.id, false);
            setSortedList(currentPageData);
            notification.error({
                message: _T(err.name)
            });
        });
    }
    const sortedIds = useMemo(() => currentPageData ? currentPageData.map((a) => a.schedule_id) : [], [
        currentPageData,
    ]);
    if (!currentPageData || defaultActiveKeys === null) {
        return null;
    }
    return (React.createElement(DndContext, { onDragEnd: handleDragEnd, collisionDetection: closestCenter, sensors: sensors },
        React.createElement(SortableContext, { id: event_id, items: sortedIds, strategy: verticalListSortingStrategy },
            React.createElement(List, { size: 'small', itemLayout: 'vertical', dataSource: sortedList, renderItem: (scheduleItem) => isEditing ? (React.createElement(SortableScheduleListItem, { event_id: event_id, scheduleItem: scheduleItem, defaultExpanded: defaultActiveKeys.includes(scheduleItem.schedule_id), editable: true })) : (React.createElement(ScheduleListItem, { event_id: event_id, scheduleItem: scheduleItem, defaultExpanded: defaultActiveKeys.includes(scheduleItem.schedule_id), editable: false, disabled: disabled })) }))));
};
const SortableScheduleListItem = (props) => {
    const { event_id, scheduleItem, defaultExpanded, editable } = props;
    const { attributes, listeners, setNodeRef, transform, transition, } = useSortable({
        id: scheduleItem.schedule_id,
        data: {
            type: 'schedule'
        }
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (React.createElement(ScheduleListItem, { event_id: event_id, scheduleItem: scheduleItem, defaultExpanded: defaultExpanded, editable: editable, setNodeRef: setNodeRef, style: style, attributes: attributes, listeners: listeners }));
};
const ScheduleListItem = (props) => {
    const { event_id, scheduleItem, defaultExpanded, editable, setNodeRef, style, attributes, listeners, disabled } = props;
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [editing, setEditing] = useState(false);
    const hasRounds = ScheduleItem.hasRounds(scheduleItem);
    const handleClickExpand = () => {
        if (hasRounds) {
            setExpanded(!expanded);
        }
    };
    const handleClickEdit = (event) => {
        event.stopPropagation();
        setEditing(true);
    };
    const PanelStatus = (props) => {
        const item = props.scheduleItem;
        const badgeStatus = () => {
            if (item.schedule_rounds_ready > 0) {
                return "error";
            }
            if (item.schedule_rounds_confirmed > 0 && item.schedule_rounds_confirmed === item.schedule_rounds_total) {
                return "success";
            }
            if (item.schedule_rounds_confirmed > 0 && item.schedule_rounds_confirmed < item.schedule_rounds_total) {
                return "warning";
            }
            return "default";
        };
        return React.createElement(Badge, { status: badgeStatus() });
    };
    return (React.createElement(List.Item, { ref: setNodeRef, style: { ...style, padding: 0 }, ...attributes },
        React.createElement(List.Item.Meta, { style: { marginBottom: 0 }, description: React.createElement(Row, { justify: 'space-between', onClick: handleClickExpand, style: { padding: 8 } },
                React.createElement(Space, null,
                    editable && React.createElement(MenuOutlined, { ...listeners, style: { cursor: "grab", color: "#999" } }),
                    React.createElement(Button, { type: "text", onClick: handleClickExpand },
                        React.createElement(Space, null,
                            React.createElement("span", { style: { fontWeight: scheduleItem.schedule_type != 'comment' ? 'bold' : 'normal' } },
                                getFormattedTime(scheduleItem.schedule_start_time),
                                scheduleItem.schedule_start_time ? ' - ' : '',
                                scheduleItem.schedule_value),
                            hasRounds &&
                                React.createElement(Badge, { count: scheduleItem.schedule_rounds_total, overflowCount: 999 }))),
                    hasRounds &&
                        React.createElement(PanelStatus, { scheduleItem: scheduleItem }),
                    !disabled && scheduleItem.schedule_id != 'null' &&
                        React.createElement(EditOutlined, { onClick: handleClickEdit }),
                    scheduleItem.schedule_access_level > 0 &&
                        React.createElement(Text, { type: "warning" }, _T('schedule.details.schedule_access_level.1')),
                    scheduleItem.dragLoading && React.createElement(Spin, null)),
                !editable && hasRounds ? (expanded ? React.createElement(DownOutlined, { onClick: handleClickExpand }) : React.createElement(RightOutlined, { onClick: handleClickExpand })) : React.createElement("div", { style: { minWidth: 14 } })) }),
        !editable && expanded &&
            React.createElement(ScheduleItemView, { event_id: event_id, scheduleItem: scheduleItem, disabled: disabled }),
        editing &&
            React.createElement(ScheduleItemEdit, { event_id: event_id, scheduleItem: scheduleItem, visible: editing, setVisible: () => setEditing(false), disabled: disabled })));
};
